import { escapeHtml, fromScaled, toScaled } from '@colplat/ui-web';
import type { AccountDto } from './webledgerOfflineState.js';

/**
 * The chart of accounts as a tree, with subtree totals — legacy's `tree.js`
 * arithmetic, kept out of the rendering so it can be tested without a DOM.
 *
 * The server sends each account's *own* balances and does not roll them up,
 * deliberately: rolling up is the client's job because the client is what
 * knows which subtrees are collapsed and which view is showing. All sums here
 * are exact (scaled `BigInt`) for the same reason the reports' are — these
 * are balances someone reconciles against a bank.
 */

export interface AccountNode {
  account: AccountDto;
  children: AccountNode[];
  depth: number;
  /** This account's own balances, per currency. Decimal text. */
  own: Record<string, string>;
  /** Own plus every descendant's, per currency — what the total cell shows. */
  subtree: Record<string, string>;
}

/** Siblings in `sortOrder` then name, as legacy re-sorts them client-side. */
function compareAccounts(a: AccountDto, b: AccountDto): number {
  return (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name);
}

function addInto(target: Record<string, bigint>, balances: Record<string, string>): void {
  for (const [code, value] of Object.entries(balances)) {
    target[code] = (target[code] ?? 0n) + toScaled(value);
  }
}

function asText(scaled: Record<string, bigint>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [code, value] of Object.entries(scaled)) out[code] = fromScaled(value);
  return out;
}

/**
 * Build the forest and roll balances up.
 *
 * An account whose `parentId` points at something missing is treated as a
 * root rather than dropped. Losing an account from the chart entirely because
 * its parent was deleted would hide money; showing it at the top is wrong
 * about the hierarchy but right about the total, and visible enough to fix.
 */
export function buildAccountTree(accounts: readonly AccountDto[]): AccountNode[] {
  const byId = new Map(accounts.map((a) => [a.id, a] as const));
  const childrenOf = new Map<string | null, AccountDto[]>();
  for (const account of accounts) {
    const parent = account.parentId && byId.has(account.parentId) ? account.parentId : null;
    childrenOf.set(parent, [...(childrenOf.get(parent) ?? []), account]);
  }

  const build = (account: AccountDto, depth: number, seen: Set<string>): AccountNode => {
    // Cycle guard: `parentId` is an ordinary editable field, so a user can
    // make an account its own ancestor. Without this the recursion never ends.
    const nextSeen = new Set(seen).add(account.id);
    const children = (childrenOf.get(account.id) ?? [])
      .filter((child) => !nextSeen.has(child.id))
      .sort(compareAccounts)
      .map((child) => build(child, depth + 1, nextSeen));

    const own = account.balances ?? {};
    const subtree: Record<string, bigint> = {};
    addInto(subtree, own);
    for (const child of children) addInto(subtree, child.subtree);
    return { account, children, depth, own, subtree: asText(subtree) };
  };

  return (childrenOf.get(null) ?? []).sort(compareAccounts).map((root) => build(root, 0, new Set()));
}

/** Depth-first, skipping the children of anything collapsed — what the table actually renders. */
export function visibleRows(nodes: readonly AccountNode[], collapsed: ReadonlySet<string>): AccountNode[] {
  const out: AccountNode[] = [];
  const walk = (list: readonly AccountNode[]): void => {
    for (const node of list) {
      out.push(node);
      if (!collapsed.has(node.account.id)) walk(node.children);
    }
  };
  walk(nodes);
  return out;
}

/** Every account that has children — what "collapse all" needs. */
export function collapsibleIds(nodes: readonly AccountNode[]): string[] {
  const out: string[] = [];
  const walk = (list: readonly AccountNode[]): void => {
    for (const node of list) {
      if (node.children.length > 0) out.push(node.account.id);
      walk(node.children);
    }
  };
  walk(nodes);
  return out;
}

/** The chain of ancestor ids, so returning to an account can open the branch it lives in. */
export function ancestorIds(accounts: readonly AccountDto[], id: string): string[] {
  const byId = new Map(accounts.map((a) => [a.id, a] as const));
  const out: string[] = [];
  let node = byId.get(id)?.parentId ?? null;
  for (let hops = 0; node && hops < 64; hops += 1) {
    out.push(node);
    node = byId.get(node)?.parentId ?? null;
  }
  return out;
}

/**
 * Whether to show the "own" line under an account.
 *
 * Only when it both posts directly *and* has sub-accounts — legacy's rule,
 * and a good one: with no children the own figure and the subtree total are
 * the same number, and printing it twice is how a reader starts wondering
 * which of the two they should trust.
 */
export function showsOwnLine(node: AccountNode): boolean {
  return node.children.length > 0 && Object.keys(node.own).some((code) => toScaled(node.own[code]!) !== 0n);
}

// ---------------------------------------------------------------- by purpose

export interface PurposeGroup {
  purposeId: string;
  name: string;
  iconPath: string;
  accounts: AccountDto[];
  /** Summed own balances of the members — not subtree totals: a purpose is a tag, not a hierarchy. */
  totals: Record<string, string>;
}

/**
 * The "By purpose" view: accounts grouped by tag rather than by position.
 *
 * Sums the members' *own* balances, never their subtrees. A purpose is a set
 * of accounts, and if a tagged parent and its tagged child were both counted
 * with their subtrees the child's money would land in the total twice.
 */
export function groupByPurpose(
  accounts: readonly AccountDto[],
  purposes: ReadonlyArray<{ id: string; name: string; iconPath?: string }>,
): PurposeGroup[] {
  return purposes.map((purpose) => {
    const members = accounts
      .filter((a) => (a.purposeIds ?? []).includes(purpose.id))
      .sort((a, b) => a.name.localeCompare(b.name));
    const totals: Record<string, bigint> = {};
    for (const member of members) addInto(totals, member.balances ?? {});
    return {
      purposeId: purpose.id, name: purpose.name, iconPath: purpose.iconPath ?? '',
      accounts: members, totals: asText(totals),
    };
  }).filter((group) => group.accounts.length > 0);
}

/** What `accountPath` joins with, and what the short label splits back on. */
export const PATH_SEP = ' ➤ ';

export interface ShortAccountLabel {
  /** The last two segments — what to show. */
  label: string;
  /** The whole chain — what to put in a tooltip and an accessible name. */
  full: string;
}

/**
 * Just enough of the path to tell an account from a same-named one elsewhere
 * in the tree: the last two segments, legacy's `acctShortLabel`.
 *
 * A field is a few hundred pixels wide and a path in this book runs to three
 * or four segments, so the full one is cut off by the field — and what gets
 * cut is the END, which is the only part that differs. "Assets ➤ Asset Loan
 * Tracking Renov. ➤ Maint." arrives as "Assets ➤ Asset Loan Tracking Ren…",
 * which is the half every account shares. Two segments name the account and
 * say which of the same-named ones it is, and that is the question the label
 * has to answer; the tree behind the button answers the rest.
 */
export function accountShortLabel(accounts: readonly AccountDto[], id: string): ShortAccountLabel {
  const full = accountPath(accounts, id);
  return { label: full.split(PATH_SEP).slice(-2).join(PATH_SEP), full };
}

/**
 * A short label as markup: the leading segment muted, the account's own name
 * plain. Legacy's `acctLabelHtml` — the path is context, not the name, and
 * showing it at the same weight makes every field read as a sentence about
 * Assets.
 *
 * The lead is a separate element so it can be the part that gets ellipsised
 * when the field is too narrow: the tail is what distinguishes one account
 * from another, so it is the tail that must survive.
 */
export function accountLabelHtml(label: string): string {
  const parts = label.split(PATH_SEP);
  const leaf = escapeHtml(parts.pop() ?? '');
  if (parts.length === 0) return `<span class="wl-acct-leaf">${leaf}</span>`;
  return `<span class="wl-acct-lead">${escapeHtml(parts.join(PATH_SEP) + PATH_SEP)}</span>`
    + `<span class="wl-acct-leaf">${leaf}</span>`;
}

/** The full `Assets ➤ Current ➤ Cash` chain — what the purpose view shows instead of indentation. */
export function accountPath(accounts: readonly AccountDto[], id: string, separator = PATH_SEP): string {
  const byId = new Map(accounts.map((a) => [a.id, a] as const));
  const chain: string[] = [];
  let node = byId.get(id);
  for (let hops = 0; node && hops < 64; hops += 1) {
    chain.unshift(node.name);
    node = node.parentId ? byId.get(node.parentId) : undefined;
  }
  return chain.join(separator);
}
